import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';
import { useDesignSession } from '../contexts/DesignSessionContext';
import CustomDesignWrapper from '../components/design/CustomDesignWrapper';

/**
 * CustomDesignPage
 * 
 * Page component for the custom design flow.
 * Handles:
 * - Product data fetching
 * - Design session validation
 * - Navigation flow (before/after order details)
 */
export default function CustomDesignPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const {
        sessionId,
        productSlug,
        initSession,
        completeDesign,
        saveToFirebase,
        designData
    } = useDesignSession();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check if this is "after order details" flow from location state
    const isAfterFlow = location.state?.afterOrderDetails === true;
    const pendingOrderData = location.state?.orderData || null;

    // Fetch product data
    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            setError(null);
            try {
                const q = query(
                    collection(db, 'products'),
                    where('slug', '==', slug),
                    where('status', '==', 'published')
                );
                const snapshot = await getDocs(q);

                if (!snapshot.empty) {
                    const productData = {
                        id: snapshot.docs[0].id,
                        ...snapshot.docs[0].data()
                    };

                    // Verify custom design page is enabled for this product
                    if (!productData.customDesignPage?.enabled) {
                        setError('Custom design is not enabled for this product.');
                        return;
                    }

                    setProduct(productData);

                    // Initialize session if not already for this product
                    if (!sessionId || productSlug !== slug) {
                        initSession(productData);
                    }
                } else {
                    setError('Product not found.');
                }
            } catch (err) {
                console.error('Error fetching product:', err);
                setError('Failed to load product. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [slug, sessionId, productSlug, initSession]);

    // Handle design completion
    const handleDesignComplete = async (finalDesignData) => {
        try {
            // Mark design as complete
            completeDesign();

            // Determine next step based on flow
            const position = product?.customDesignPage?.position || 'before';

            if (position === 'before' || !isAfterFlow) {
                // Design is before order details - navigate to product page to show order form
                navigate(`/products/${slug}?order=true`, {
                    state: { fromDesign: true }
                });
            } else {
                // Design is after order details - complete the order
                // Save design to Firebase and include in order
                const designResult = await saveToFirebase();

                // Navigate to order completion with design data
                navigate(`/products/${slug}`, {
                    state: {
                        completeOrder: true,
                        orderData: pendingOrderData,
                        designData: {
                            designUrl: designResult.designUrl,
                            previewUrl: designResult.previewUrl,
                            sessionId: designResult.sessionId
                        }
                    }
                });
            }
        } catch (error) {
            console.error('Error completing design:', error);
            throw error;
        }
    };

    // Handle back navigation
    const handleBack = () => {
        if (isAfterFlow) {
            // Going back to order form - preserve order data
            navigate(`/products/${slug}?order=true`, {
                state: {
                    returnFromDesign: true,
                    orderData: pendingOrderData
                }
            });
        } else {
            // Going back to product page
            navigate(`/products/${slug}`);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading designer...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
                <div className="max-w-md text-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Oops!
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                    <button
                        onClick={() => navigate(`/products/${slug}`)}
                        className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                    >
                        Back to Product
                    </button>
                </div>
            </div>
        );
    }

    // No product
    if (!product) {
        return null;
    }

    return (
        <>
            <Helmet>
                <title>{product.customDesignPage?.title || 'Design'} | {product.name}</title>
                <meta name="robots" content="noindex" />
            </Helmet>

            <CustomDesignWrapper
                product={product}
                onComplete={handleDesignComplete}
                onBack={handleBack}
                initialDesignData={designData}
            />
        </>
    );
}
